import type * as React from 'react'

import {
  Columns3Icon,
  Combobox,
  EmptyState,
  SearchIcon,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import type { JSX } from 'react'

export const FilterByIdeaGroup_IdeaGroup = graphql(/* GraphQL */ `
  fragment FilterByIdeaGroup_IdeaGroup on IdeaGroup @_unmask {
    id
    name
  }
`)

/**
 * Masked type provided by parent query result.
 * Use `useFilterByTagFragment` to get the unmasked type.
 */
export type MaskedFilterByIdeaGroupFragment = readonly FragmentOf<
  typeof FilterByIdeaGroup_IdeaGroup
>[]

/**
 * Unmasked type extracted from parent query result.
 */
export type FilterByIdeaGroupFragment = FragmentOf<
  typeof FilterByIdeaGroup_IdeaGroup
>
/**
 * List of unmasked tag filter fragments extracted from parent query result.
 */
export type FilterByIdeaGroupFragments = readonly FragmentOf<
  typeof FilterByIdeaGroup_IdeaGroup
>[]

/**
 * Unmask tag filters from the parent query result into typed
 *  fragments.
 * @param tags masked tag filter fragments from the parent query result
 * @returns
 */
export const useFilterByIdeaGroupFragment = (
  groups: MaskedFilterByIdeaGroupFragment,
): FilterByIdeaGroupFragments => groups

type FilterByIdeaGroupProps = {
  groups: MaskedFilterByIdeaGroupFragment
  /**
   * Selected group ids
   */
  value: string[]
  onSelect: (groupIds: string[]) => void
  children: React.ReactNode
}

/**
 * Component to enable filtering by idea groups, using Combobox.
 * Accepts a composable trigger via children, similar to Radix patterns.
 */
export const FilterByIdeaGroup = ({
  groups: passedGroups,
  value,
  onSelect,
  children,
}: FilterByIdeaGroupProps): JSX.Element => {
  const groups = passedGroups

  return (
    <Combobox multiple value={value} onChange={onSelect}>
      <Combobox.Trigger>{children}</Combobox.Trigger>
      <Combobox.Content>
        <Combobox.Input placeholder="Search groups" prefix={<SearchIcon />} />
        <Combobox.List>
          <Combobox.Empty>
            {groups.length === 0 ? (
              <EmptyState size="small">
                <EmptyState.Icon>
                  <Columns3Icon />
                </EmptyState.Icon>
                <EmptyState.Heading>No groups</EmptyState.Heading>
                <EmptyState.Description>
                  Use groups to organise your ideas
                </EmptyState.Description>
              </EmptyState>
            ) : (
              <EmptyState size="small">
                <EmptyState.Icon>
                  <SearchIcon />
                </EmptyState.Icon>
                <EmptyState.Description>No groups found</EmptyState.Description>
              </EmptyState>
            )}
          </Combobox.Empty>
          {groups.map(({ id, name }) => (
            <Combobox.Item key={id} value={id} keywords={[name]}>
              {name}
            </Combobox.Item>
          ))}
        </Combobox.List>
      </Combobox.Content>
    </Combobox>
  )
}
